import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Vendor, CreateVendorForm, Category } from '../../types';
import PhoneInput from './PhoneInput';

const vendorSchema = yup.object({
  name: yup.string().required('Name is required').max(100, 'Name must be less than 100 characters'),
  address: yup.string().required('Address is required').max(255, 'Address must be less than 255 characters'),
  city: yup.string().required('City is required').max(100, 'City must be less than 100 characters'),
  state: yup.string().required('State is required').max(50, 'State must be less than 50 characters'),
  zipCode: yup.string().required('Zip code is required').max(20, 'Zip code must be less than 20 characters'),
  phone: yup.string().required('Phone is required').max(20, 'Phone must be less than 20 characters'),
  email: yup.string().email('Invalid email format').max(256, 'Email must be less than 256 characters').optional(),
  website: yup.string().url('Invalid URL format').max(500, 'Website must be less than 500 characters').optional(),
  notes: yup.string().max(2000, 'Notes must be less than 2000 characters').optional(),
  rank: yup.number().min(0, 'Rank must be 0 or greater').optional(),
  rating: yup.number().min(0, 'Rating must be 0 or greater').max(5, 'Rating must be 5 or less').optional(),
  latitude: yup.number().min(-90, 'Latitude must be between -90 and 90').max(90, 'Latitude must be between -90 and 90').optional(),
  longitude: yup.number().min(-180, 'Longitude must be between -180 and 180').max(180, 'Longitude must be between -180 and 180').optional(),
  isActive: yup.boolean().required('Active status is required'),
  categoryIds: yup.array().of(yup.number().required()).min(1, 'At least one category must be selected').required('Categories are required')
});

interface VendorFormProps {
  vendor?: Vendor;
  categories: Category[];
  onSubmit: (data: CreateVendorForm) => void;
  isLoading?: boolean;
  error?: string;
  defaultZipCode?: string;
}

const VendorForm: React.FC<VendorFormProps> = ({ vendor, categories, onSubmit, isLoading = false, error, defaultZipCode }) => {
  
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset
  } = useForm<CreateVendorForm>({
    resolver: yupResolver(vendorSchema),
    defaultValues: {
      name: vendor?.name || '',
      address: vendor?.address || '',
      city: vendor?.city || '',
      state: vendor?.state || '',
      zipCode: vendor?.zipCode || defaultZipCode || '',
      phone: vendor?.phone || '',
      email: vendor?.email || '',
      website: vendor?.website || '',
      notes: vendor?.notes || '',
      rank: vendor?.rank || 0,
      rating: typeof vendor?.rating === 'number' ? vendor.rating : undefined,
      latitude: vendor?.latitude || undefined,
      longitude: vendor?.longitude || undefined,
      isActive: vendor?.isActive ?? true,
      categoryIds: vendor?.categories?.map(c => c.id) || []
    }
  });

  // Reset form when vendor changes
  useEffect(() => {
    reset({
      name: vendor?.name || '',
      address: vendor?.address || '',
      city: vendor?.city || '',
      state: vendor?.state || '',
      zipCode: vendor?.zipCode || defaultZipCode || '',
      phone: vendor?.phone || '',
      email: vendor?.email || '',
      website: vendor?.website || '',
      notes: vendor?.notes || '',
      rank: vendor?.rank || 0,
      rating: typeof vendor?.rating === 'number' ? vendor.rating : undefined,
      isActive: vendor?.isActive ?? true,
      categoryIds: vendor?.categories?.map(c => c.id) || []
    });
  }, [vendor, defaultZipCode, reset]);

  const renderStars = (rating: number | undefined) => {
    const numRating = typeof rating === 'number' ? rating : parseFloat(rating as any) || 0;
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`w-5 h-5 ${
              star <= numRating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        <span className="ml-2 text-sm text-gray-600">{numRating.toFixed(1)}</span>
      </div>
    );
  };

  const handleFormSubmit = (data: CreateVendorForm) => {
    // Convert empty strings to undefined for optional fields to pass backend validation
    const cleanedData = {
      ...data,
      email: data.email?.trim() || undefined,
      website: data.website?.trim() || undefined,
      notes: data.notes?.trim() || undefined,
      rank: data.rank || 0,
      rating: data.rating || undefined,
      // Let the API geocode from zip — avoid sending 0/0 placeholders
      latitude: undefined,
      longitude: undefined
    };
    
    onSubmit(cleanedData);
  };

  try {
    return (
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Active Status - moved to top */}
      <div className="pb-4 border-b border-gray-200">
        <Controller
          name="isActive"
          control={control}
          render={({ field: { onChange, value, ref } }) => (
            <div className="flex items-center">
              <input
                ref={ref}
                type="checkbox"
                id="isActive"
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
                className="h-4 w-4 text-accent-color focus:ring-accent-color border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                Active
              </label>
            </div>
          )}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Name <span className="text-red-500">*</span>
          </label>
                     <input
             type="text"
             id="name"
             {...register('name')}
             className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
             placeholder="Enter vendor name"
           />
          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
        </div>

                 {/* Phone */}
         <div>
           <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
             Phone <span className="text-red-500">*</span>
           </label>
           <Controller
             name="phone"
             control={control}
             render={({ field }) => (
               <PhoneInput
                 value={field.value || ''}
                 onChange={field.onChange}
                 className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
                 placeholder="Enter phone number"
                 id="phone"
                 name="phone"
               />
             )}
           />
           {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>}
         </div>

        {/* Address */}
        <div className="md:col-span-2">
          <label htmlFor="address" className="block text-sm font-medium text-gray-700">
            Address <span className="text-red-500">*</span>
          </label>
                     <input
             type="text"
             id="address"
             {...register('address')}
             className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
             placeholder="Enter address"
           />
          {errors.address && <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>}
        </div>

        {/* City */}
        <div>
          <label htmlFor="city" className="block text-sm font-medium text-gray-700">
            City <span className="text-red-500">*</span>
          </label>
                     <input
             type="text"
             id="city"
             {...register('city')}
             className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
             placeholder="Enter city"
           />
          {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>}
        </div>

        {/* State */}
        <div>
          <label htmlFor="state" className="block text-sm font-medium text-gray-700">
            State <span className="text-red-500">*</span>
          </label>
          <select
            id="state"
            {...register('state')}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
          >
            <option value="">Select State</option>
            <option value="AL">Alabama (AL)</option>
            <option value="AK">Alaska (AK)</option>
            <option value="AZ">Arizona (AZ)</option>
            <option value="AR">Arkansas (AR)</option>
            <option value="CA">California (CA)</option>
            <option value="CO">Colorado (CO)</option>
            <option value="CT">Connecticut (CT)</option>
            <option value="DE">Delaware (DE)</option>
            <option value="FL">Florida (FL)</option>
            <option value="GA">Georgia (GA)</option>
            <option value="HI">Hawaii (HI)</option>
            <option value="ID">Idaho (ID)</option>
            <option value="IL">Illinois (IL)</option>
            <option value="IN">Indiana (IN)</option>
            <option value="IA">Iowa (IA)</option>
            <option value="KS">Kansas (KS)</option>
            <option value="KY">Kentucky (KY)</option>
            <option value="LA">Louisiana (LA)</option>
            <option value="ME">Maine (ME)</option>
            <option value="MD">Maryland (MD)</option>
            <option value="MA">Massachusetts (MA)</option>
            <option value="MI">Michigan (MI)</option>
            <option value="MN">Minnesota (MN)</option>
            <option value="MS">Mississippi (MS)</option>
            <option value="MO">Missouri (MO)</option>
            <option value="MT">Montana (MT)</option>
            <option value="NE">Nebraska (NE)</option>
            <option value="NV">Nevada (NV)</option>
            <option value="NH">New Hampshire (NH)</option>
            <option value="NJ">New Jersey (NJ)</option>
            <option value="NM">New Mexico (NM)</option>
            <option value="NY">New York (NY)</option>
            <option value="NC">North Carolina (NC)</option>
            <option value="ND">North Dakota (ND)</option>
            <option value="OH">Ohio (OH)</option>
            <option value="OK">Oklahoma (OK)</option>
            <option value="OR">Oregon (OR)</option>
            <option value="PA">Pennsylvania (PA)</option>
            <option value="RI">Rhode Island (RI)</option>
            <option value="SC">South Carolina (SC)</option>
            <option value="SD">South Dakota (SD)</option>
            <option value="TN">Tennessee (TN)</option>
            <option value="TX">Texas (TX)</option>
            <option value="UT">Utah (UT)</option>
            <option value="VT">Vermont (VT)</option>
            <option value="VA">Virginia (VA)</option>
            <option value="WA">Washington (WA)</option>
            <option value="WV">West Virginia (WV)</option>
            <option value="WI">Wisconsin (WI)</option>
            <option value="WY">Wyoming (WY)</option>
            <option value="DC">District of Columbia (DC)</option>
          </select>
          {errors.state && <p className="mt-1 text-sm text-red-600">{errors.state.message}</p>}
        </div>

        {/* Zip Code */}
        <div>
          <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700">
            Zip Code <span className="text-red-500">*</span>
          </label>
                     <input
             type="text"
             id="zipCode"
             {...register('zipCode')}
             className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
             placeholder="Enter zip code"
           />
          {errors.zipCode && <p className="mt-1 text-sm text-red-600">{errors.zipCode.message}</p>}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email
          </label>
                     <input
             type="email"
             id="email"
             {...register('email')}
             className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
             placeholder="Enter email address"
           />
          {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
        </div>

        {/* Website */}
        <div>
          <label htmlFor="website" className="block text-sm font-medium text-gray-700">
            Website
          </label>
                     <input
             type="url"
             id="website"
             {...register('website')}
             placeholder="https://example.com"
             className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
           />
          {errors.website && <p className="mt-1 text-sm text-red-600">{errors.website.message}</p>}
        </div>

        {/* Rank */}
        <div>
          <label htmlFor="rank" className="block text-sm font-medium text-gray-700">
            Rank
          </label>
                     <input
             type="number"
             id="rank"
             {...register('rank', { valueAsNumber: true })}
             min="0"
             className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
             placeholder="Enter rank"
           />
          {errors.rank && <p className="mt-1 text-sm text-red-600">{errors.rank.message}</p>}
        </div>

        {/* Rating */}
        <div>
          <label htmlFor="rating" className="block text-sm font-medium text-gray-700">
            Rating
          </label>
          <Controller
            name="rating"
            control={control}
            render={({ field }) => (
              <div className="mt-1">
                                 <input
                   type="number"
                   id="rating"
                   value={field.value || ''}
                   min="0"
                   max="5"
                   step="0.1"
                   className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
                   placeholder="Enter rating (0-5)"
                   onChange={(e) => {
                     const value = parseFloat(e.target.value);
                     if (isNaN(value)) {
                       field.onChange(0);
                     } else if (value >= 0 && value <= 5) {
                       field.onChange(value);
                     }
                   }}
                   onBlur={field.onBlur}
                 />
                                 {field.value && field.value > 0 && (
                   <div className="mt-2">
                     {renderStars(field.value)}
                   </div>
                 )}
              </div>
            )}
          />
          {errors.rating && <p className="mt-1 text-sm text-red-600">{errors.rating.message}</p>}
        </div>

        {/* Coordinates are derived from zip code on the server; do not submit placeholders. */}
      </div>

      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Notes
        </label>
        <textarea
          id="notes"
          rows={4}
          {...register('notes')}
          className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
          placeholder="Internal notes about this vendor (not visible to the vendor)"
        />
        {errors.notes && <p className="mt-1 text-sm text-red-600">{errors.notes.message}</p>}
      </div>

      {/* Categories */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Categories <span className="text-red-500">*</span>
        </label>
        <Controller
          name="categoryIds"
          control={control}
          render={({ field }) => (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3">
              {categories.map((category) => (
                <label key={category.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    value={category.id}
                    checked={field.value.includes(category.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        field.onChange([...field.value, category.id]);
                      } else {
                        field.onChange(field.value.filter(id => id !== category.id));
                      }
                    }}
                    className="h-4 w-4 text-accent-color focus:ring-accent-color border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-900">{category.name}</span>
                </label>
              ))}
            </div>
          )}
        />
        {errors.categoryIds && <p className="mt-1 text-sm text-red-600">{errors.categoryIds.message}</p>}
      </div>


             {/* Submit Button */}
       <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
         <button
           type="submit"
           disabled={isLoading}
           className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
         >
           {isLoading ? (
             <div className="flex items-center">
               <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
               {vendor ? 'Updating...' : 'Creating...'}
             </div>
           ) : (
             vendor ? 'Update Vendor' : 'Create Vendor'
           )}
         </button>
       </div>
      </form>
    );
  } catch (error) {
    console.error('VendorForm error:', error);
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        <h3 className="font-medium">Error loading vendor form</h3>
        <p className="text-sm mt-1">Please refresh the page and try again.</p>
        <details className="mt-2">
          <summary className="cursor-pointer text-sm">Technical details</summary>
          <pre className="text-xs mt-1 overflow-auto">{error instanceof Error ? error.message : String(error)}</pre>
        </details>
      </div>
    );
  }
};

export default VendorForm;
